import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { useLoginDBMutation } from "../app/apiSlices/dbSlice";
import { useLoginClientMutation } from "../app/apiSlices/clientSlice";
import { useDispatch, useSelector } from "react-redux";
import { login as LOGINDISPATCH } from "../app/userSlice";
import { toast } from "react-hot-toast";
import { LoginPayload } from "../app/userSlice";
import { RootState } from "../app/store";
import { SignUp } from "../assets";

const SignIn = () => {
  const { loggedIn, role, subregion_id } = useSelector(
    (state: RootState) => state.user
  );
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isClient, setIsClient] = useState(false);
  const [passwordShown, setPasswordShown] = useState(false);
  const [login, { isLoading }] = useLoginDBMutation();
  const [loginClient, { isLoading: ClientLoading }] = useLoginClientMutation();
  const dispatch = useDispatch();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isLoading) return;
    if (!email || !password) return;
    if (isClient) {
      clientLogin();
    } else {
      dbLogin();
    }
  };

  const dbLogin = async () => {
    if (isLoading) return;
    try {
      const res = await login({ email, password }).unwrap();
      const object: LoginPayload = {
        _id: res.data.user.id,
        email: res.data.user.email,
        name: res.data.user.name,
        role: undefined,
        subregion_id: res.data.user.subregion_id,
        access_token: res.data.tokens.accessToken,
        refresh_token: res.data.tokens.refreshToken,
      };
      dispatch(LOGINDISPATCH(object));
      setEmail("");
      setPassword("");
      navigate("/db/");
    } catch (error: any) {
      toast.error(error?.data?.message ?? "Something went wrong");
    }
  };

  const clientLogin = async () => {
    if (ClientLoading) return;
    try {
      const res = await loginClient({ email, password }).unwrap();
      const object: LoginPayload = {
        _id: res.data.user.id,
        email: res.data.user.email,
        name: res.data.user.name,
        role: res.data.user.role,
        subregion_id: undefined,
        access_token: res.data.tokens.accessToken,
        refresh_token: res.data.tokens.refreshToken,
      };
      dispatch(LOGINDISPATCH(object));
      setEmail("");
      setPassword("");
      navigate("/client/");
    } catch (error: any) {
      toast.error(error?.data?.message ?? "Something went wrong");
    }
  };

  useEffect(() => {
    if (loggedIn && subregion_id) {
      navigate("/db/");
    }
    if (loggedIn && role) {
      navigate("/client/");
    }
  }, [loggedIn, navigate, role, subregion_id]);
  return (
    <section className="w-screen h-screen overflow-hidden grid grid-flow-row grid-row-1 lg:grid-cols-7">
      <div className="flex flex-col items-center md:items-start pt-20 md:pt-32 w-full lg:col-span-3 px-4 md:px-18 lg:px-24">
        <h1 className="text-semibold text-3xl md:text-4xl pb-3">Welcome</h1>
        <p className="text-sm text-gray-600 pb-6">
          Enter your email and special key to sign in
        </p>
        <form
          onSubmit={handleSubmit}
          className="flex flex-col items-center justify-center gap-4 w-full max-w-[400px]"
        >
          <div className="flex flex-col w-full gap-1">
            <label className="form-label" htmlFor="email">
              Email
            </label>
            <input
              className="form-input"
              type="email"
              name="email"
              id="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="flex flex-col w-full gap-1">
            <label className="form-label" htmlFor="password">
              Password
            </label>
            <div className="flex items-center w-full relative">
              <input
                className="form-input"
                type={passwordShown ? "text" : "password"}
                name="password"
                id="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <div className="absolute right-2.5 top-2.5">
                {passwordShown ? (
                  <EyeSlashIcon
                    className="h-5 w-5 text-gray-400 hover:text-gray-500 cursor-pointer"
                    onClick={() => setPasswordShown(false)}
                  />
                ) : (
                  <EyeIcon
                    className="h-5 w-5 text-gray-400 hover:text-gray-500 cursor-pointer"
                    onClick={() => setPasswordShown(true)}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-2 items-center mr-auto">
            <input
              type="checkbox"
              name="isClient"
              id="isClient"
              checked={isClient}
              onChange={() => setIsClient(!isClient)}
              className="text-slate-800 focus:ring-slate-800 border-gray-300 rounded"
            />
            <label htmlFor="isClient" className="form-label">
              Login as Client or Agency
            </label>
          </div>

          <button
            type="submit"
            className="my-2 py-2 px-4 w-full inline-flex items-center justify-center border border-transparent text-base font-medium rounded-md text-white bg-slate-800 hover:bg-slate-90"
          >
            {isLoading || ClientLoading ? (
              <>
                <div
                  role="status"
                  className="flex justify-center gap-2 items-center"
                >
                  <svg
                    aria-hidden="true"
                    className=" w-8 h-6 text-gray-200 animate-spin  fill-white"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill"
                    />
                  </svg>{" "}
                  Logging in
                  <span className="sr-only">Logging in...</span>
                </div>
              </>
            ) : (
              "Login"
            )}
          </button>
        </form>
      </div>
      <div className="hidden lg:flex lg:col-span-4 h-full">
        <img
          src={SignUp}
          alt=""
          className="w-full h-full object-center object-contain"
        />
      </div>
    </section>
  );
};

export default SignIn;
